import { isKnown } from './assessmentFacts.js';
import { strokesOf, listBoards, BOARD_STEP } from './illustrations.js';
import { STEP_ORDER, SLIDE_LAYOUT, buildPages, visiblePage, pageDigest, fingerprint } from './assessmentPages.js';

// The takeaway: what the prospect leaves with.
//
// The same pages the meeting showed, in the same order and at the same fixed
// size, built from the same page models. So a drawing made in the room is
// printed on the page it was made on, as if it had been drawn on paper, and a
// page with ink on it is otherwise identical to a page without (Jimmy,
// 2026-09-29). Nothing of the application is in the artwork: no navigation,
// no toolbar, no advisor furniture.
//
// Two rules shape everything here.
//
// A drawing is shown over the figures it was drawn over. It is tied to its
// page by a digest of what the page showed, so when a figure has changed since
// the ink went on, the page prints with today's figures and the drawing is
// kept at the end with the page it was actually made on, rather than sitting
// over numbers it was not drawn about.
//
// The review fingerprint is taken over the content, not over counts of it: the
// text and figures that print, and the full stroke data of every drawing.

const NOT_A_PLAN = 'Assessment, not a full plan';

// A moment drawing belongs on its page when it was made in the slide layout
// and the page still shows exactly what it showed then.
function belongsOn(ill, step, digest) {
  return ill.kind === 'moment'
    && ill.step === step
    && (ill.basis?.layoutVersion || 0) >= SLIDE_LAYOUT
    && ill.basis?.page === digest;
}

function sheet(i) {
  return {
    id: i.id,
    title: i.title?.trim() || '',
    drawingKind: i.kind,
    step: i.step,
    board: i.board,
    basis: i.basis || null,
    capture: i.capture || null,
    strokes: strokesOf(i),
    updated: i.updated || '',
  };
}

/**
 * Which illustrations the packet carries. Everything drawn is included unless
 * the advisor has taken one out, because a drawing made in the room is part of
 * the conversation by default.
 */
export function takeawayIncludes(illustrations, overrides = {}) {
  const set = new Set();
  for (const i of illustrations) {
    if (!strokesOf(i).length) continue;
    const override = overrides[i.id];
    if (override === false) continue;
    set.add(i.id);
  }
  return set;
}

export function buildTakeaway({ facts, analysis = null, illustrations = [], prepare = {}, view, asOf = '', demo = false }) {
  const included = takeawayIncludes(illustrations, prepare.takeawayExclude || {});
  const drawn = illustrations.filter(i => included.has(i.id) && strokesOf(i).length);
  const models = buildPages({ facts, view, analysis });
  const used = new Set();

  const pages = [];
  for (const step of STEP_ORDER) {
    // The whiteboards go at the back, after everything there is to read.
    if (step === BOARD_STEP) continue;
    const model = models[step];
    if (!model) continue;
    // The tax page is only worth a sheet of paper when there is money to show.
    if (step === 'tax' && !(view.taxPools.total > 0)) continue;
    const digest = pageDigest(model);
    const ink = drawn.filter(i => belongsOn(i, step, digest)).pop() || null;
    if (ink) used.add(ink.id);
    pages.push({
      kind: 'slide',
      step,
      model: visiblePage(model),
      ink: ink ? { id: ink.id, strokes: strokesOf(ink), updated: ink.updated || '' } : null,
    });
  }

  // Every whiteboard drawn in the meeting, in the order it was started, each
  // on a page of its own at the back (Jimmy, 2026-10-03). A sheet nobody drew
  // on is not printed.
  for (const { key, illustration } of listBoards({ illustrations: drawn })) {
    if ((illustration.basis?.layoutVersion || 0) < SLIDE_LAYOUT) continue;
    used.add(illustration.id);
    pages.push({
      kind: 'slide',
      step: BOARD_STEP,
      board: key,
      model: visiblePage(models[BOARD_STEP]),
      ink: { id: illustration.id, strokes: strokesOf(illustration), updated: illustration.updated || '' },
    });
  }

  // What is left: the older titled whiteboards, drawings made over figures
  // that have since changed, and drawings from before the slide layout.
  for (const i of drawn) {
    if (used.has(i.id)) continue;
    const slide = i.kind === 'moment' && (i.basis?.layoutVersion || 0) >= SLIDE_LAYOUT && i.capture;
    pages.push({ kind: slide ? 'earlier' : 'illustration', ...sheet(i) });
  }

  return {
    name: facts.name,
    demo,
    asOf,
    footerLeft: NOT_A_PLAN,
    pages: pages.map((p, i) => ({ ...p, page: i + 1, of: pages.length })),
  };
}

/**
 * The fingerprint the review is taken against.
 *
 * Deliberately over the printed content. Counting strokes, or hashing a handful
 * of headline figures, lets a real change slip past a review that says it
 * covered it, which is the defect this replaces.
 */
export function takeawayDigest(takeaway) {
  return fingerprint(JSON.stringify({ name: takeaway.name, demo: takeaway.demo, pages: takeaway.pages }));
}

export function isReviewCurrent(reviewed, digest) {
  return Boolean(reviewed && digest && reviewed.digest === digest);
}

/**
 * What stops a packet going out at all, as against what merely needs saying.
 */
export function takeawayLimitations(facts, comparison, analysis) {
  const out = [];
  if (!isKnown(facts.assets) && !isKnown(facts.income) && !isKnown(facts.spending)) {
    out.push({ key: 'empty', blocking: true, text: 'Nothing has been recorded yet, so there is no packet to send.' });
  }
  if (!comparison.ready) {
    out.push({ key: 'comparison', blocking: false, text: 'The income and spending comparison is withheld, and the packet says so.' });
  }
  if (facts.assetsPartial) {
    out.push({ key: 'assets', blocking: false, text: 'The asset list is not confirmed complete, so the net of recorded items is withheld.' });
  }
  if (analysis && analysis.availability?.state === 'unreviewed') {
    out.push({ key: 'analysis', blocking: true, text: 'A statement analysis is loaded but has not been accepted, so it cannot be sent.' });
  }
  return out;
}
